import React, { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useReducedMotion } from '../hooks/useReducedMotion';

gsap.registerPlugin(ScrollTrigger);

const days = [
  {
    day: 'DAY 01',
    title: 'Opening',
    date: 'February 15, 2027',
    events: [
      { time: '08:00', title: 'Registration', desc: 'Team check-in and materials distribution' },
      { time: '09:00', title: 'Opening Ceremony', desc: 'Welcome speeches and competition overview' },
      { time: '10:30', title: 'Preliminary Rounds', desc: 'First round of competition judging' },
      { time: '12:00', title: 'Networking Lunch', desc: 'Exhibition viewing and connections' },
      { time: '13:30', title: 'Technical Workshops', desc: 'Parallel sessions on robotics topics' },
    ],
  },
  {
    day: 'DAY 02',
    title: 'Competition',
    date: 'February 16, 2027',
    events: [
      { time: '09:00', title: 'Main Competition', desc: 'Teams present solutions to judges' },
      { time: '12:00', title: 'Keynote Lunch', desc: 'Special address from industry leaders' },
      { time: '13:30', title: 'Final Demonstrations', desc: 'Top teams compete in finale' },
      { time: '16:00', title: 'Panel Discussion', desc: 'Future of Robotics with experts' },
    ],
  },
  {
    day: 'DAY 03',
    title: 'Final & Awards',
    date: 'February 17, 2027',
    events: [
      { time: '10:00', title: 'Award Ceremony', desc: 'Winners announced and prizes awarded' },
      { time: '11:30', title: 'Closing Reception', desc: 'Celebration and networking' },
      { time: '13:00', title: 'Public Exhibition', desc: 'Open showcase of all entries' },
    ],
  },
];

const Schedule = () => {
  const sectionRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) return;

    const ctx = gsap.context(() => {
      gsap.utils.toArray('.schedule-day').forEach((day, i) => {
        gsap.fromTo(
          day,
          { opacity: 0, y: 20 },
          {
            opacity: 1,
            y: 0,
            duration: 0.5,
            ease: 'power2.out',
            delay: i * 0.1,
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top 70%',
            },
          }
        );
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [prefersReducedMotion]);

  return (
    <section
      id="schedule"
      ref={sectionRef}
      className="section-padding bg-surface-secondary dark:bg-dark-surface-secondary relative overflow-hidden"
    >
      <div className="section-container">
        {/* Header */}
        <div className="mb-16 lg:mb-20">
          <span className="tech-label text-accent dark:text-dark-accent mb-6 block">
            SCHEDULE / 2027
          </span>
          <h2 className="section-title mb-6">Three days of competition.</h2>
          <p className="section-desc">
            Learning, and networking with robotics enthusiasts worldwide.
          </p>
        </div>

        {/* Structured list — no gradient headers */}
        <div className="grid lg:grid-cols-3 gap-8">
          {days.map((day) => (
            <div
              key={day.day}
              className="schedule-day border border-border dark:border-dark-border rounded-card bg-surface dark:bg-dark-surface overflow-hidden"
            >
              {/* Day header — typography only */}
              <div className="px-6 py-5 border-b border-border dark:border-dark-border">
                <span className="tech-label text-accent dark:text-dark-accent block mb-1">
                  {day.day}
                </span>
                <h3 className="text-lg font-semibold text-text-primary dark:text-dark-text-primary">
                  {day.title}
                </h3>
                <p className="text-sm text-text-muted dark:text-dark-text-muted mt-1">
                  {day.date}
                </p>
              </div>

              {/* Events — structured list */}
              <div className="divide-y divide-border dark:divide-dark-border">
                {day.events.map((event, j) => (
                  <div
                    key={j}
                    className="px-6 py-4 hover:bg-surface-secondary dark:hover:bg-dark-surface-secondary transition-colors duration-150"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-14 text-right">
                        <span className="font-mono text-xs font-medium text-accent dark:text-dark-accent">
                          {event.time}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <h4 className="text-sm font-semibold text-text-primary dark:text-dark-text-primary">
                          {event.title}
                        </h4>
                        <p className="text-xs text-text-secondary dark:text-dark-text-secondary mt-0.5">
                          {event.desc}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Schedule;